import React from 'react';
import { motion, Variants } from 'framer-motion';

type RevealDirection = 'up' | 'down' | 'left' | 'right' | 'none';

interface RevealProps {
  children: React.ReactNode;
  className?: string;
  direction?: RevealDirection;
  delay?: number;
  duration?: number;
  distance?: number;
  once?: boolean;
  as?: 'div' | 'section' | 'article' | 'li';
}

const offsetFor = (direction: RevealDirection, distance: number) => {
  switch (direction) {
    case 'up':
      return { y: distance };
    case 'down':
      return { y: -distance };
    case 'left':
      return { x: distance };
    case 'right':
      return { x: -distance };
    default:
      return {};
  }
};

/**
 * Scroll-triggered fade/slide-in reveal. This is the site's single primitive
 * for "content appears as you scroll to it" - use it instead of ad hoc
 * IntersectionObserver code or Tailwind's animate-fade-in-up (which fires on
 * mount, not on scroll) so reveal timing/easing stays consistent everywhere.
 */
export const Reveal: React.FC<RevealProps> = ({
  children,
  className = '',
  direction = 'up',
  delay = 0,
  duration = 0.6,
  distance = 24,
  once = true,
  as = 'div',
}) => {
  const variants: Variants = {
    hidden: { opacity: 0, ...offsetFor(direction, distance) },
    visible: { opacity: 1, x: 0, y: 0 },
  };

  const MotionTag = motion[as];

  return (
    <MotionTag
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once, amount: 0.2 }}
      variants={variants}
      transition={{ duration, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </MotionTag>
  );
};
